import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Dumbbell, Newspaper, ArrowUpRight, Loader2, Activity } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIP_MAP = [
  { label: "For Runners", category: 0 },
  { label: "Core", categoryName: "Abs" },
  { label: "Legs", categoryName: "Legs" },
  { label: "Calves", categoryName: "Calves" },
  { label: "Back", categoryName: "Back" },
  { label: "Cardio", categoryName: "Cardio" },
];

const TRAINING_PLAN_GROUPS = [
  {
    category: "Running Plans",
    icon: Activity,
    plans: [
      { title: "5K Speed & Base", duration: "6 weeks", weekly: "4 days · 2 easy runs, 1 speed session, 1 long run", difficulty: "Beginner", description: "Build consistent mileage, learn controlled pacing and finish your first or fastest 5K feeling strong." },
      { title: "10K Endurance", duration: "8 weeks", weekly: "4 days · intervals, tempo, recovery and long run", difficulty: "Beginner+", description: "A steady bridge from 5K fitness to confident 10K racing, with aerobic volume and pacing discipline." },
      { title: "Half Marathon Mastery", duration: "12 weeks", weekly: "5 days · speed, tempo, strength and long run", difficulty: "Intermediate", description: "Structured half-marathon preparation with cutback weeks, race-pace work and a two-week taper." },
      { title: "Full Marathon Builder", duration: "16 weeks", weekly: "5–6 days · quality sessions, fueling practice and long runs", difficulty: "Advanced", description: "Progressive marathon mileage with durability work, race nutrition rehearsal and recovery built in." },
    ],
  },
  {
    category: "Strength for Runners",
    icon: Dumbbell,
    plans: [
      { title: "Core & Glutes Foundation", duration: "6 weeks", weekly: "3 days · 25-minute runner-specific strength", difficulty: "All levels", description: "Improve posture, hip stability and hill strength with simple sessions that fit around running." },
      { title: "Lower Body Power", duration: "8 weeks", weekly: "3 days · strength, plyometrics and mobility", difficulty: "Intermediate", description: "Develop resilient calves, hamstrings and glutes for faster finishes and fewer niggles." },
    ],
  },
  {
    category: "Triathlon Introduction",
    icon: Activity,
    plans: [
      { title: "Sprint Triathlon Ready", duration: "8 weeks", weekly: "5 days · swim, bike, run and brick session", difficulty: "Beginner", description: "A friendly first-triathlon pathway covering basic swim confidence, bike handling and run transitions." },
      { title: "Olympic Distance Foundations", duration: "12 weeks", weekly: "6 days · two swims, two rides, two runs", difficulty: "Intermediate", description: "Balanced endurance preparation for athletes moving from running into multisport racing." },
    ],
  },
];

export default function TrainingHub() {
  const [taxonomy, setTaxonomy] = useState([]);
  const [chips, setChips] = useState([]);
  const [active, setActive] = useState(0);
  const [exercises, setExercises] = useState(null);
  const [news, setNews] = useState(null);

  useEffect(() => {
    axios.get(`${API}/fitness/taxonomy`).then((r) => {
      const cats = r.data.categories || [];
      setTaxonomy(cats);
      setChips(
        CHIP_MAP.map((c) => ({
          ...c,
          id: c.categoryName ? (cats.find((x) => x.name === c.categoryName) || {}).id || 0 : 0,
        }))
      );
    }).catch(() => {});
    axios.get(`${API}/runner-news`).then((r) => setNews(r.data.items || [])).catch(() => setNews([]));
  }, []);

  useEffect(() => {
    if (!chips.length) return;
    setExercises(null);
    const chip = chips[active];
    const qs = chip.id ? `category=${chip.id}&` : "";
    axios.get(`${API}/fitness/exercises?${qs}limit=12`).then((r) => setExercises(r.data.exercises || [])).catch(() => setExercises([]));
  }, [active, chips]);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-28 sm:pt-36 pb-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-14">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <Activity className="w-4 h-4" /> Training Hub
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#0D1117] tracking-tight mb-6">
            Stronger legs. Longer <span className="italic text-[#FF4E00]">miles.</span>
          </h1>
          <p className="text-base text-[#374151] max-w-2xl leading-relaxed">
            Strength and mobility guidance curated for runners — powered by the open wger exercise library —
            plus the latest headlines from Runner's World for every level, beginner to advanced.
          </p>
        </motion.div>

        <section className="mb-20" data-testid="training-plans-section">
          <div className="mb-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-3">Training Plans</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0D1117] tracking-tight">Pick a goal. Follow the structure.</h2>
            <p className="text-sm text-[#6B7280] mt-2 max-w-2xl">Content-led plans for now — no personalisation engine. Each plan gives you duration, weekly rhythm and difficulty before you ask a coach to adapt it.</p>
          </div>
          <div className="space-y-10">
            {TRAINING_PLAN_GROUPS.map((group) => {
              const GroupIcon = group.icon;
              return (
                <div key={group.category}>
                  <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-[#374151] mb-5 flex items-center gap-2">
                    <GroupIcon className="w-4 h-4 text-[#FF4E00]" /> {group.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                    {group.plans.map((plan) => (
                      <motion.article
                        key={plan.title}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.5 }}
                        className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 hover:border-[#FF4E00]/40 transition-colors duration-300 flex flex-col"
                        data-testid="training-plan-card"
                      >
                        <div className="flex flex-wrap gap-2 mb-5">
                          <span className="font-mono text-[9px] uppercase tracking-[0.16em] bg-[#FF4E00]/10 text-[#FF4E00] px-3 py-1.5 rounded-full">{plan.duration}</span>
                          <span className="font-mono text-[9px] uppercase tracking-[0.16em] border border-[#0D1117]/10 text-[#374151] px-3 py-1.5 rounded-full">{plan.difficulty}</span>
                        </div>
                        <h4 className="font-serif text-xl text-[#0D1117] mb-3">{plan.title}</h4>
                        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#6B7280] leading-relaxed mb-4">{plan.weekly}</p>
                        <p className="text-sm text-[#374151] leading-relaxed flex-1">{plan.description}</p>
                        <Link
                          to="/contact"
                          onClick={() => sessionStorage.setItem("fg_enquiry", JSON.stringify({ realm: "Coaching & Training Plans", message: `I'm interested in the ${plan.title} plan (${plan.duration}). Please help me get coached.` }))}
                          data-testid={`plan-coached-${plan.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="mt-6 inline-flex items-center justify-center gap-2 border border-[#FF4E00]/50 hover:border-[#FF4E00] text-[#FF4E00] hover:bg-[#FF4E00] hover:text-[#FBF9F5] font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-3 rounded-full transition-all duration-300"
                        >
                          Get Coached <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                      </motion.article>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="mb-20" data-testid="strength-section">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#0D1117] tracking-tight mb-2 flex items-center gap-3">
            <Dumbbell className="w-5 h-5 text-[#FF4E00]" /> Strength & Mobility Library
          </h2>
          <p className="text-sm text-[#6B7280] mb-8">Filter by area — every move matters for injury-free running.</p>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="training-chips">
            {chips.map((c, i) => (
              <button
                key={c.label}
                onClick={() => setActive(i)}
                data-testid={`training-chip-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`font-mono text-[11px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                  active === i
                    ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                    : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {exercises === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#FF4E00]" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {exercises.map((ex, i) => (
                <motion.article
                  key={ex.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] overflow-hidden hover:border-[#FF4E00]/40 transition-colors duration-300"
                  data-testid="exercise-card"
                >
                  {ex.image ? (
                    <div className="aspect-[4/3] bg-[#F4F2EC] flex items-center justify-center p-4">
                      <img src={ex.image} alt={ex.name} loading="lazy" className="max-h-full object-contain" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-[#F4F2EC] flex items-center justify-center">
                      <Dumbbell className="w-10 h-10 text-[#FF4E00]/40" />
                    </div>
                  )}
                  <div className="p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00] mb-2">
                      {ex.category}{ex.muscles.length ? ` · ${ex.muscles.slice(0, 2).join(", ")}` : ""}
                    </p>
                    <h3 className="font-serif text-lg text-[#0D1117] mb-2">{ex.name}</h3>
                    <p className="text-sm text-[#374151] leading-relaxed line-clamp-4">{ex.description}</p>
                  </div>
                </motion.article>
              ))}
              {exercises.length === 0 && <p className="text-sm text-[#6B7280]">No exercises found for this filter.</p>}
            </div>
          )}
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mt-6">
            Exercise data &amp; images: wger.de — individual records retain their listed authors and licenses (CC-BY-SA)
          </p>
        </div>

        <div data-testid="runner-news-section">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#0D1117] tracking-tight mb-2 flex items-center gap-3">
            <Newspaper className="w-5 h-5 text-[#FF4E00]" /> From Runner's World
          </h2>
          <p className="text-sm text-[#6B7280] mb-8">Beginner to advanced — the latest running journalism, linking straight to the source.</p>
          {news === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#FF4E00]" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {news.map((n, i) => (
                <motion.a
                  key={n.url}
                  href={n.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  className="group rounded-3xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1"
                  data-testid="news-card"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-lg text-[#0D1117] group-hover:text-[#FF4E00] transition-colors duration-300">{n.title}</h3>
                    <ArrowUpRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#FF4E00] shrink-0 mt-1 transition-all duration-300 group-hover:rotate-45" />
                  </div>
                  <p className="text-sm text-[#374151] leading-relaxed mt-3 line-clamp-3">{n.excerpt}</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mt-4">Read at Runner's World</p>
                </motion.a>
              ))}
              {news.length === 0 && <p className="text-sm text-[#6B7280]">Headlines are taking a breather — check back soon.</p>}
            </div>
          )}
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mt-6">
            Headlines &amp; excerpts: Runner's World (runnersworld.com) — links open the original articles
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
